import { computed, ref } from '@vue/composition-api';
import dayjs from 'dayjs';
import { DataTableHeader } from 'vuetify';

import { PriorityTag } from '@/API';
import {
  toLocaleDatetimeString,
  toLocaleString,
  toISOString,
  isValidDateFormat,
} from '@/containers/globals/Util';
import { i18n } from '@/plugins/i18n/i18n';
import { Announcement, AnnouncementForDisplay, ItemField } from '@/types/type';
import {
  getStartDateTime,
  getEndDateTime,
  isAfterToday,
  millisecondsToSeconds,
} from '@/utils/AnnouncementUtils';
import { newPortalDate } from '@/utils/PortalDate';

const getHeaderText = (name: string) =>
  i18n.tc(`commonSetting.announcementTableColumn.${name}`);

enum PriorityTagValue {
  'High',
  'Medium',
  'Low',
  '高',
  '中',
  '低',
}

const getHeaders = (): DataTableHeader[] => [
  {
    text: getHeaderText('title'),
    value: 'title',
    width: '30%',
  },
  {
    text: getHeaderText('isPublic'),
    value: 'display__isPublic',
  },
  {
    text: getHeaderText('displayStartDate'),
    value: 'display__displayStartDate',
  },
  {
    text: getHeaderText('displayEndDate'),
    value: 'display__displayEndDate',
  },
  {
    text: getHeaderText('priorityTag'),
    value: 'display__priorityTag',
    sort: (priorityTag1: string, priorityTag2: string) => {
      const priorityTagKeys = Object.keys(PriorityTagValue);
      return (
        priorityTagKeys.indexOf(priorityTag2) -
        priorityTagKeys.indexOf(priorityTag1)
      );
    },
  },
  {
    text: getHeaderText('updatedAt'),
    value: 'display__updatedAt',
  },
  {
    text: '',
    value: 'actions',
    sortable: false,
  },
];

const getAnnouncementFields = (): ItemField[] => {
  const required = (value: any) => {
    const isEmpty = value === '' || value === null || value === undefined;
    return !isEmpty || i18n.tc('commonSetting.error.required');
  };

  const contentRequired = (value: string) => {
    const matchTags = /(<([^>]+)>)/gi;
    const tagRemoved = value.replace(matchTags, '');
    return !!tagRemoved || i18n.tc('commonSetting.error.required');
  };

  const isValidDate = (value: string) => {
    if (value) {
      return (
        isValidDateFormat(value) ||
        `${value}${i18n.tc('commonSetting.error.invalidDate')}`
      );
    }
    return true;
  };

  const endDateValidation = (value: string) => {
    if (toISOString(value)) {
      const epochSeconds = newPortalDate(value, 'YYYY/MM/DD').toEpochSeconds();
      return (
        isAfterToday(getEndDateTime(epochSeconds)) ||
        i18n.tc('commonSetting.error.afterTodayDate')
      );
    }
    return true;
  };

  const dateValidation = (item: Announcement | AnnouncementForDisplay) => {
    const startDate = item.displayStartDate;
    const endDate = item.displayEndDate
      ? getEndDateTime(item.displayEndDate)
      : 0;
    if (startDate && endDate) {
      return (
        startDate <= endDate || i18n.tc('commonSetting.error.beforeStartDate')
      );
    }
    return true;
  };

  return [
    {
      label: getHeaderText('title'),
      value: 'title',
      cols: 12,
      rules: [required],
    },
    {
      label: getHeaderText('content'),
      value: 'content',
      tag: 'editor',
      cols: 12,
      rules: [contentRequired],
    },
    {
      label: getHeaderText('isPublic'),
      value: 'isPublic',
      radioItems: [
        {
          key: true,
          label: i18n.tc('commonSetting.isPublicLabel.public'),
        },
        {
          key: false,
          label: i18n.tc('commonSetting.isPublicLabel.private'),
        },
      ],
      cols: 12,
      rules: [required],
    },
    {
      label: getHeaderText('priorityTag'),
      value: 'priorityTag',
      radioItems: [
        {
          key: PriorityTag.High,
          label: i18n.tc('commonSetting.priorityTagLabel.high'),
        },
        {
          key: PriorityTag.Medium,
          label: i18n.tc('commonSetting.priorityTagLabel.medium'),
        },
        {
          key: PriorityTag.Low,
          label: i18n.tc('commonSetting.priorityTagLabel.low'),
        },
      ],
      cols: 12,
      rules: [required],
    },
    {
      label: getHeaderText('displayStartDate'),
      value: 'displayStartDate',
      tag: 'date-picker',
      rules: [required, isValidDate],
    },
    {
      label: getHeaderText('displayEndDate'),
      value: 'displayEndDate',
      tag: 'date-picker',
      rules: [
        isValidDate,
        endDateValidation,
        (_, item: Announcement) => dateValidation(item),
      ],
    },
  ];
};

const defaultItem = {
  title: '',
  content: '',
  isPublic: false,
  displayStartDate: getStartDateTime(
    millisecondsToSeconds(new Date().getTime())
  ),
  displayEndDate: null,
  priorityTag: PriorityTag.Medium,
};

const toAnnouncementForDisplay = (
  announcements: Announcement[]
): AnnouncementForDisplay[] =>
  announcements.map(val =>
    val
      ? {
          ...val,
          display__isPublic: getRadioLabel(val, 'isPublic'),
          display__displayStartDate: toLocaleString(val.displayStartDate),
          display__displayEndDate: toLocaleString(val.displayEndDate),
          display__priorityTag: getRadioLabel(val, 'priorityTag'),
          display__updatedAt: toLocaleDatetimeString(val.updatedAt!),
          isReadOnly: isPastEpochSeconds(val.displayEndDate),
        }
      : ({} as AnnouncementForDisplay)
  );

export const isPastEpochSeconds = (seconds: number | null) => {
  return seconds !== null && dayjs.unix(seconds).isBefore(dayjs());
};

const getRadioLabel = <T extends { [x: string]: any }>(
  obj: T,
  key: keyof T
) => {
  const field = getAnnouncementFields().find(field => field.value === key);
  const radioItem =
    field &&
    field.radioItems &&
    field.radioItems.find(radioItem => radioItem.key === obj[key]);
  return radioItem ? radioItem.label : '';
};

export const useAnnouncementTable = () => {
  const sortBy: keyof AnnouncementForDisplay = 'display__updatedAt';
  const sortDesc = true;
  const announcements = ref([] as AnnouncementForDisplay[]);
  const headers = computed(getHeaders);

  const updateAnnouncements = async (value: Announcement[]) =>
    (announcements.value = toAnnouncementForDisplay(value));

  return {
    announcements,
    getAnnouncementFields,
    headers,
    defaultItem,
    sortBy,
    sortDesc,
    updateAnnouncements,
  };
};
